import React, { useEffect, useState, useRef, useCallback } from 'react'
import { withRouter } from 'react-router-dom'
import { Switch } from '@softwaredev_cegeka/stix-form'
import { DataTableSortableHeader } from '@softwaredev_cegeka/stix-data-table'
import { Menu, Badge } from '@softwaredev_cegeka/stix-core'
import {
    debouncedLoadData,
    sortObjectToString,
    sortData,
} from 'src/utils/utils'

import './entityTable.scss'
import EntityTable from './EntityTable'

/**
 * Group table content.
 * @param props
 * @returns {*}
 * @constructor
 */
function GroupTable(props) {
    const { data, loading, setParams, rowSelectHandler, params } = props
    const didInitialSearch = useRef(false)
    const columnsToSearch = useRef(['name']).current
    const handleGlobalFilter = useCallback(
        value => {
            setSearch(value)
            // check all columns on entry
            columnsToSearch.forEach(col => {
                params[col + '_like'] = value.toString()
            })
            params.use_or = true
            debouncedLoadData(params, setParams)
        },
        [columnsToSearch, params, setParams]
    )

    let [sort, setSort] = useState({ column: null, direction: null })
    let [search, setSearch] = useState('')

    useEffect(() => {
        // [didInitialSearch] is used to prevent infinite rendering. Once the code inside the second
        // if statement is run, the [didInitialSearch] becomes true and the code inside the if won't
        // be triggered anymore
        if (!didInitialSearch.current) {
            if (window.location.search !== '') {
                const urlSearchParams = new URLSearchParams(
                    window.location.search
                )
                urlSearchParams.forEach((value, key) => {
                    if (key === 'groupId') {
                        const selectedObj = { original: { id: value } } // This object styling is needed to pass it to rowSelectHandler
                        rowSelectHandler(selectedObj)
                    }
                    if (key === 'search') {
                        handleGlobalFilter(value)
                    }
                })

                didInitialSearch.current = true
            }
        }
    }, [rowSelectHandler, handleGlobalFilter])

    const columns = [
        {
            Header: () => {
                return (
                    <DataTableSortableHeader
                        sorted={
                            sort.column === 'name' ? sort.direction : undefined
                        }
                        onSort={handleColumnSort('name')}
                    >
                        Group Name
                    </DataTableSortableHeader>
                )
            },
            accessor: 'name',
            width: '30%',
            Cell: row => {
                return (
                    <div className={'ad-group-label'}>
                        {row.value}
                        {Array.isArray(row.row.original.mapped_ad_groups) &&
                        row.row.original.mapped_ad_groups.length ? (
                            <Badge size={'normal'} variant={'info'}>
                                AD linked
                            </Badge>
                        ) : null}
                        {row.row.original.isPublic ? (
                            <Badge size={'normal'} variant={'info'}>
                                Public
                            </Badge>
                        ) : null}
                    </div>
                )
            },
        },
        {
            Header: () => (
                <DataTableSortableHeader
                    sorted={
                        sort.column === 'tenant' ? sort.direction : undefined
                    }
                    onSort={handleColumnSort('tenant')}
                >
                    Tenant
                </DataTableSortableHeader>
            ),
            accessor: 'tenant',
            Cell: ({ cell }) => (cell.value ? cell.value.name : ''),
            width: '25%',
        },
        {
            Header: () => {
                return (
                    <DataTableSortableHeader
                        sorted={
                            sort.column === 'company_name'
                                ? sort.direction
                                : undefined
                        }
                        onSort={handleColumnSort('company_name')}
                    >
                        Company
                    </DataTableSortableHeader>
                )
            },
            accessor: 'company',
            Cell: ({ cell }) => (cell.value ? cell.value.name : ''),
            width: '25%',
        },
        {
            Header: 'Default',
            accessor: 'isDefault',
            disableFilters: true,
            Cell: ({ cell }) => {
                const switchProps = {
                    checked: cell.value ? true : null,
                    disabled: true,
                    srOnly: true,
                    label: 'Default group',
                }
                return <Switch {...switchProps} />
            },
            width: '10%',
        },
    ]
    const columnsToFilter = [
        {
            column: 'company',
        },
        {
            column: 'name',
        },
        {
            column: 'tenant',
        },
    ]

    const handleColumnSort = columnName => sortingDirection => {
        sortData(columnName, sortingDirection, params, setSort, setParams)
    }

    function handleFilterChange(entries) {
        // Reset to show al results after clear filters is clicked.
        if (entries.length === 0) {
            setParams({})
            return
        }

        const sortString = sortObjectToString(sort)

        // Update params with new selected filter and send them back to the route.
        entries.forEach(item => {
            Object.entries(item).forEach(([key, value]) => {
                params[key + '_like'] = value.toString()
            })
        })

        if (sortString) {
            params.sort = sortString
        }

        setParams(params)
    }

    if (process.env.REACT_APP_ENABLE_EDIT_GROUP_FUNCTIONALITY === 'true') {
        columns.push({
            Header: () => null,
            id: 'menu',
            Cell: selectedRow => (
                <Menu.Option
                    contextMenu={[
                        {
                            name: 'Edit group',
                            icon: 'edit',
                            onClick: () => {
                                props.history.push(
                                    '/group-form/' + selectedRow.row.original.id
                                )
                            },
                        },
                        {
                            name: 'See details',
                            icon: 'info',
                            onClick: () => {
                                rowSelectHandler(selectedRow.row)
                            },
                        },
                        {
                            name: 'Remove',
                            icon: 'delete',
                            onClick: () => {
                                let r = window.confirm(
                                    `You are permanently removing group ${selectedRow.row.original.name}. Are you sure?`
                                )
                                if (r === true) {
                                    props.deleteAction(
                                        selectedRow.row.original.id,
                                        selectedRow.row.original.name
                                    )
                                }
                            },
                        },
                    ]}
                    icon={'actions-vert'}
                />
            ),
            width: '1%',
        })
    }
    return (
        <EntityTable
            loading={loading}
            onSingleRowSelect={selectedRowObj => {
                rowSelectHandler(selectedRowObj.selectedRow)
            }}
            data={data}
            columns={columns}
            columnsTofilter={columnsToFilter}
            onColumnFilter={handleFilterChange}
            onGlobalFilter={handleGlobalFilter}
            globalFilter={search}
        />
    )
}

export default withRouter(GroupTable)
